import { Injectable } from '@angular/core';
import {HttpService} from "./http.service";
import { CreateResult } from "../models/create-result";
import { CardAcceptorConfigViewModel } from "../models/card-acceptor-config-view-model";
import { LimitsClassViewModel } from '../models/limits-class-view-model';
import { RoutingGroupViewModel } from '../models/routing-group-view-model';
import { CountryViewModel } from '../models/country-view-model';
import { RegionViewModel } from '../models/region-view-model';
import { SupportTeamsViewModel } from '../models/support-teams-view-model';
import { LanguagesViewModel } from '../models/languages-view-model';
import { TAMerchantStatusViewModel } from '../models/ta-merchant-status-view-model';
import { ReceiptGroupViewModel } from '../models/receipt-group-view-model';
import { MerchantDetailModel } from '../models/merchant-detail-model';
import {TmCardAcceptorConfigViewModel} from "../models/tm-card-acceptor-model";
import { BehaviorSubject, Observable } from 'rxjs';
import { PagedResponse } from '../models/paged-response';
import { SearchFilterModel } from '../models/search-filter-model';

@Injectable({
  providedIn: 'root'
})
export class CardAcceptorConfigService {

  private countSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get count(): number {
    return this.countSubject.value;
  }

  public set count(value) {
    this.countSubject.next(value);
  }

  public get countObservable(): Observable<number> {
    return this.countSubject.asObservable();
  }

  constructor(private _httpService: HttpService) {
    this.InitCount();
  }

  public async Get(configId: string): Promise<CardAcceptorConfigViewModel> {
    const httpResult = await this._httpService.apiGet<CardAcceptorConfigViewModel>(`cardAcceptorConfig/${configId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetAllConfigs(): Promise<CardAcceptorConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<CardAcceptorConfigViewModel[]>(`cardAcceptorConfig/all`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async AllConfigs(): Promise<CardAcceptorConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<CardAcceptorConfigViewModel[]>(`cardAcceptorConfig/AllConfigs`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetByParticipant(participantId: number): Promise<CardAcceptorConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<CardAcceptorConfigViewModel[]>(`cardAcceptorConfig/ByParticipant/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetPagedConfigs(filter: SearchFilterModel): Promise<PagedResponse<CardAcceptorConfigViewModel>> {
    const httpResult = await this._httpService.apiPost<PagedResponse<CardAcceptorConfigViewModel>>(`cardAcceptorConfig/paged`, filter);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return {
      response: null,
      total: 0
    };
  }

  public async InitCount(): Promise<void> {
    this.countSubject = new BehaviorSubject<number>(0);
    const httpResult = await this._httpService.apiGet<number>(`cardAcceptorConfig/count`);
    if (httpResult && httpResult.body) {
      this.count = httpResult.body;
    }
  }

  public async GetParticipantConfigs(): Promise<CardAcceptorConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<CardAcceptorConfigViewModel[]>(`cardAcceptorConfig/p`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async Update(cardAcceptorConfig: CardAcceptorConfigViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`cardAcceptorConfig/${cardAcceptorConfig.id}`, cardAcceptorConfig);
    if (httpResult && httpResult.body) {
      this.InitCount();
      return httpResult.body;
    }
    return {message: "An unknown error occured", success: false};
  }

  public async Add(merchant: CardAcceptorConfigViewModel): Promise<CreateResult> {

    const httpResult = await this._httpService.apiPost<CreateResult>(`cardAcceptorConfig/add`, merchant);
    if (httpResult && httpResult.body) {
      this.InitCount();
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetLimitsClasses(): Promise<LimitsClassViewModel[]> {
    const httpResult = await this._httpService.apiGet<LimitsClassViewModel[]>(`cardAcceptorConfig/limitsClasses`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRoutingGroups(): Promise<RoutingGroupViewModel[]> {
    const httpResult = await this._httpService.apiGet<RoutingGroupViewModel[]>(`cardAcceptorConfig/routingGroups`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCountries(): Promise<CountryViewModel[]> {
    const httpResult = await this._httpService.apiGet<CountryViewModel[]>(`cardAcceptorConfig/countries`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRegions(): Promise<RegionViewModel[]> {
    const httpResult = await this._httpService.apiGet<RegionViewModel[]>(`cardAcceptorConfig/regions`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetSupportTeams(): Promise<SupportTeamsViewModel[]> {
    const httpResult = await this._httpService.apiGet<SupportTeamsViewModel[]>(`cardAcceptorConfig/supportTeams`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetLanguages(): Promise<LanguagesViewModel[]> {
    const httpResult = await this._httpService.apiGet<LanguagesViewModel[]>(`cardAcceptorConfig/languages`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetTAMerchantStatuses(): Promise<TAMerchantStatusViewModel[]> {
    const httpResult = await this._httpService.apiGet<TAMerchantStatusViewModel[]>(`cardAcceptorConfig/merchantStatuses`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetReceiptGroups(): Promise<ReceiptGroupViewModel[]> {
    const httpResult = await this._httpService.apiGet<ReceiptGroupViewModel[]>(`cardAcceptorConfig/receiptGroups`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async DeleteCardAcceptorConfig(configId: number): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`cardAcceptorConfig/${configId}/DeleteCardAcceptorConfig`, configId);
    if (httpResult && httpResult.body) {
      this.InitCount();
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetAllMerchantDetails(configId: number): Promise<MerchantDetailModel[]> {
    const httpResult = await this._httpService.apiGet<MerchantDetailModel[]>(`cardAcceptorConfig/MerchantDetails/${configId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCardAcceptorsByCardSet(cardSet: string, participantId: number): Promise<TmCardAcceptorConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<TmCardAcceptorConfigViewModel[]>(`cardAcceptorConfig/CardAcceptorsByCardSet/${cardSet}/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }
}
